// Anagrafica materiali con ricerca, CRUD e generazione QR code
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Search, Plus, Upload, Download, QrCode, X, Pencil, Hash, Loader2, Globe } from 'lucide-react'
import QRCode from 'react-qr-code'
import { PageHeader } from '../../components/layout/PageHeader'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Table } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import api from '../../lib/api'

interface Material {
  id: string
  code: string
  name: string
  description?: string
  category?: string
  unit?: string
  unitCost?: number
  quantity: number
  minStock?: number
  location?: string
  supplier?: string
  barcode?: string
  qrCode?: string
}

const materialSchema = z.object({
  code: z.string().min(1, 'Codice obbligatorio'),
  name: z.string().min(1, 'Nome obbligatorio'),
  description: z.string().optional(),
  unit: z.string().optional(),
  unitCost: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
  category: z.string().optional(),
  barcode: z.string().optional(),
  supplier: z.string().optional(),
  location: z.string().optional(),
  quantity: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().int().min(0).optional(),
  ),
  minStock: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().int().min(0).optional(),
  ),
  warehouseId: z.string().uuid().optional(),
})

type MaterialFormData = z.infer<typeof materialSchema>

interface WarehouseOption {
  id: string
  name: string
  type: 'central' | 'peripheral'
}

interface MaterialSerial {
  id: string
  materialId: string
  serialNumber: string
  partNumber?: string
  location?: string
  notes?: string
  status: 'available' | 'in_use' | 'transferred' | 'damaged'
  createdAt: string
}

interface GlobalSearchResult {
  type: 'material'
  material: { id: string; code: string; name: string; unit: string }
  warehouseStocks: Array<{ warehouseId: string; warehouseName: string; quantity: number }>
  vanStocks: Array<{ technicianId: string; technicianName: string; quantity: number }>
  matchedSerials: string[]
}

export function MaterialsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null)
  const [globalSearch, setGlobalSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null)
  const [qrMaterial, setQrMaterial] = useState<Material | null>(null)
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null)
  const qrRef = useRef<HTMLDivElement>(null)

  const { data: warehouses = [] } = useQuery<WarehouseOption[]>({
    queryKey: ['warehouses'],
    queryFn: () => api.get('/warehouse/locations').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  })

  // Peripheral warehouses first (sorted by name), then central last
  const sortedWarehouses = [...warehouses].sort((a, b) => {
    if (a.type === b.type) return a.name.localeCompare(b.name)
    return a.type === 'peripheral' ? -1 : 1
  })

  const { data: serialsData, isLoading: serialsLoading } = useQuery({
    queryKey: ['material-serials', selectedMaterialId],
    queryFn: () =>
      api
        .get<{ serials: MaterialSerial[] }>(`/warehouse/materials/${selectedMaterialId}/serials`)
        .then((r) => r.data.serials),
    enabled: !!selectedMaterialId,
  })

  const { data: searchResults, isLoading: searchLoading } = useQuery<GlobalSearchResult[]>({
    queryKey: ['warehouse-search', globalSearch],
    queryFn: () =>
      api.get('/warehouse/search', { params: { q: globalSearch } }).then((r) => r.data.results),
    enabled: globalSearch.length >= 2,
    staleTime: 30_000,
  })

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/materials/import/template', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'kleisnote-import-materiali.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      // silenzioso
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['materials', search, selectedWarehouseId],
    queryFn: async () => {
      if (!selectedWarehouseId) return { items: [] as Material[], total: 0 }
      try {
        const res = await api.get<Material[] | { items: Material[]; total: number }>('/warehouse/materials', {
          params: { warehouseId: selectedWarehouseId, search: search || undefined },
        })
        const payload = res.data
        const items = Array.isArray(payload) ? payload : (payload as { items: Material[] })?.items ?? []
        const filtered = search
          ? items.filter(
              (m) =>
                m.name?.toLowerCase().includes(search.toLowerCase()) ||
                m.code?.toLowerCase().includes(search.toLowerCase()),
            )
          : items
        return { items: filtered, total: filtered.length }
      } catch {
        return { items: [] as Material[], total: 0 }
      }
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MaterialFormData>({
    resolver: zodResolver(materialSchema),
    defaultValues: { code: '', name: '', description: '', unit: 'pz', category: '', supplier: '', barcode: '', location: '', quantity: 0, minStock: 0, warehouseId: '00000000-0000-0000-0000-000000000001' },
  })

  const openCreate = () => {
    setEditingMaterial(null)
    reset({ code: '', name: '', description: '', unit: 'pz', category: '', supplier: '', barcode: '', location: '', quantity: 0, minStock: 0, warehouseId: '00000000-0000-0000-0000-000000000001' })
    setFormOpen(true)
  }

  const openEdit = (m: Material) => {
    setEditingMaterial(m)
    reset({
      code: m.code ?? '',
      name: m.name ?? '',
      description: m.description ?? '',
      unit: m.unit ?? 'pz',
      unitCost: m.unitCost ?? undefined,
      category: m.category ?? '',
      barcode: m.barcode ?? '',
      supplier: m.supplier ?? '',
      location: m.location ?? '',
      quantity: m.quantity ?? 0,
      minStock: m.minStock ?? 0,
    })
    setFormOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async (data: MaterialFormData) => {
      if (editingMaterial?.id) {
        await api.patch(`/warehouse/materials/${editingMaterial.id}`, data)
      } else {
        await api.post('/warehouse/materials', data)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['materials'] })
      setFormOpen(false)
      setEditingMaterial(null)
    },
  })

  // Scarica QR come SVG
  const handleDownloadQr = () => {
    if (!qrRef.current || !qrMaterial) return
    const svg = qrRef.current.querySelector('svg')
    if (!svg) return
    const serializer = new XMLSerializer()
    const svgStr = serializer.serializeToString(svg)
    const blob = new Blob([svgStr], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qr-${qrMaterial.code}-${qrMaterial.name.replace(/\s+/g, '-')}.svg`
    link.click()
    URL.revokeObjectURL(url)
  }

  const columns = [
    {
      key: 'code',
      header: 'Codice',
      cell: (m: Material) => <span className="font-mono text-xs">{m.code}</span>,
      className: 'w-24',
    },
    {
      key: 'name',
      header: 'Materiale',
      cell: (m: Material) => <span className="font-medium">{m.name}</span>,
    },
    {
      key: 'category',
      header: 'Categoria',
      cell: (m: Material) => <Badge variant="default">{m.category ?? '—'}</Badge>,
      className: 'w-28',
    },
    {
      key: 'quantity',
      header: 'Pezzi',
      cell: (m: Material) => (
        <span className={(m.quantity ?? 0) <= (m.minStock ?? 0) && (m.minStock ?? 0) > 0 ? 'font-bold text-[var(--fc-danger)]' : ''}>
          {m.quantity ?? 0} {m.unit ?? 'pz'}
        </span>
      ),
      className: 'w-24',
    },
    {
      key: 'location',
      header: 'Ubicazione',
      cell: (m: Material) => {
        const serialLocations = (m as unknown as { serialLocations?: string[] }).serialLocations
        const display =
          serialLocations && serialLocations.length > 0
            ? serialLocations.join(', ')
            : (m.location ?? '—')
        return <span className="text-sm text-[var(--fc-foreground-muted)]">{display}</span>
      },
      className: 'w-32',
    },
    {
      key: 'actions',
      header: '',
      cell: (m: Material) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => openEdit(m)}
            className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
            aria-label="Modifica"
            title="Modifica"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setQrMaterial(m)}
            className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
            aria-label="Mostra QR code"
            title="Mostra QR code"
          >
            <QrCode className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSelectedMaterialId(m.id)}
            className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
            aria-label="Numeri seriali"
            title="Numeri seriali"
          >
            <Hash className="h-4 w-4" />
          </button>
        </div>
      ),
      className: 'w-24',
    },
  ]

  const textareaClass =
    'w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]'

  const qrValue = qrMaterial
    ? `FIELDCORE:MATERIAL:${qrMaterial.id}|CODE:${qrMaterial.code}|NAME:${qrMaterial.name}`
    : ''

  return (
    <div className="space-y-4">
      <PageHeader
        title="Materiali"
        breadcrumb={[{ label: 'Magazzino', href: '/warehouse' }, { label: 'Materiali' }]}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => void handleDownloadTemplate()}
            >
              Scarica template
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => navigate('/warehouse/materials/import')}
            >
              Importa Excel
            </Button>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
              Nuovo materiale
            </Button>
          </div>
        }
      />

      {/* Warehouse selector tabs */}
      <div className="border-b border-[var(--fc-border)]">
        <div className="flex gap-0 overflow-x-auto">
          {sortedWarehouses.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setSelectedWarehouseId(w.id)}
              className={[
                'whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                selectedWarehouseId === w.id
                  ? 'border-[var(--fc-primary)] text-[var(--fc-primary)]'
                  : 'border-transparent text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)] hover:border-[var(--fc-border)]',
              ].join(' ')}
            >
              {w.type === 'central' ? '🏭' : '📦'} {w.name}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar row */}
      <div className="flex items-center gap-2">
        <div className="max-w-sm flex-1">
          <Input
            placeholder="Cerca per nome o codice..."
            leftIcon={<Search className="h-4 w-4" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!selectedWarehouseId}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Globe className="h-4 w-4" />}
          onClick={() => { setSearchOpen(true); setGlobalSearch('') }}
        >
          Cerca in tutti i magazzini
        </Button>
      </div>

      {/* Materials table or empty state */}
      {!selectedWarehouseId ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-[var(--fc-border)] py-16 text-center">
          <p className="text-base font-medium text-[var(--fc-foreground-muted)]">Seleziona un magazzino</p>
          <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
            Scegli un magazzino dalle tab qui sopra per visualizzare i materiali
          </p>
        </div>
      ) : (
        <Table
          data={data?.items ?? []}
          columns={columns}
          keyExtractor={(m) => m.id}
          isLoading={isLoading}
          onRowClick={(m) => navigate(`/warehouse/materials/${m.id}`)}
          emptyMessage="Nessun materiale in questo magazzino"
        />
      )}

      {/* Modal ricerca globale */}
      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Ricerca globale magazzini"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            placeholder="Cerca materiale, codice o seriale..."
            leftIcon={<Search className="h-4 w-4" />}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            autoFocus
          />

          {globalSearch.length < 2 ? (
            <p className="py-8 text-center text-sm text-[var(--fc-foreground-muted)]">
              Digita almeno 2 caratteri per cercare
            </p>
          ) : searchLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--fc-primary)]" />
            </div>
          ) : !searchResults || searchResults.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--fc-foreground-muted)]">
              Nessun risultato per "{globalSearch}"
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
              {searchResults.map((result) => (
                <div
                  key={result.material.id}
                  className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-medium text-[var(--fc-foreground)]">{result.material.name}</span>
                      <span className="ml-2 font-mono text-xs text-[var(--fc-foreground-muted)]">{result.material.code}</span>
                    </div>
                    {result.material.unit && (
                      <Badge variant="default">{result.material.unit}</Badge>
                    )}
                  </div>

                  {result.warehouseStocks.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Magazzini</p>
                      <div className="flex flex-wrap gap-2">
                        {result.warehouseStocks.map((ws) => (
                          <span
                            key={ws.warehouseId}
                            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground)]"
                          >
                            {ws.warehouseName}
                            <span className="font-semibold text-[var(--fc-primary)]">{ws.quantity}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.vanStocks.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Furgoni tecnici</p>
                      <div className="flex flex-wrap gap-2">
                        {result.vanStocks.map((vs) => (
                          <span
                            key={vs.technicianId}
                            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground)]"
                          >
                            {vs.technicianName}
                            <span className="font-semibold text-[var(--fc-primary)]">{vs.quantity}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.matchedSerials.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Seriali corrispondenti</p>
                      <div className="flex flex-wrap gap-1">
                        {result.matchedSerials.map((sn) => (
                          <span
                            key={sn}
                            className="font-mono text-xs rounded px-1.5 py-0.5 bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground)]"
                          >
                            {sn}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal crea/modifica materiale */}
      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingMaterial(null) }}
        title={editingMaterial ? `Modifica: ${editingMaterial.name}` : 'Nuovo materiale'}
        size="lg"
      >
        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Codice *"
              {...register('code')}
              error={errors.code?.message}
              placeholder="MAT-001"
            />
            <Input
              label="Nome *"
              {...register('name')}
              error={errors.name?.message}
              placeholder="Filtro aria universale"
            />
            <Input
              label="Categoria"
              {...register('category')}
              placeholder="Filtri, Ricambi, Consumabili..."
            />
            <Input
              label="Unità di misura"
              {...register('unit')}
              placeholder="pz, kg, m, l..."
            />
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="text-sm font-medium text-[var(--fc-foreground)]">
                Magazzino di destinazione
              </label>
              <select
                {...register('warehouseId')}
                className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
              >
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.type === 'central' ? '🏭' : '📦'} {w.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--fc-foreground-muted)]">
                La quantità iniziale sarà caricata in questo magazzino
              </p>
            </div>
            <Input
              label="Pezzi disponibili"
              type="number"
              {...register('quantity')}
              error={errors.quantity?.message}
              placeholder="0"
            />
            <Input
              label="Scorta minima"
              type="number"
              {...register('minStock')}
              error={errors.minStock?.message}
              placeholder="5"
            />
            <Input
              label="Ubicazione"
              {...register('location')}
              placeholder="Scaffale A3, Magazzino Sud..."
            />
            <Input
              label="Costo unitario (€)"
              type="number"
              step="0.01"
              {...register('unitCost')}
              error={errors.unitCost?.message}
              placeholder="12.50"
            />
            <Input
              label="Fornitore"
              {...register('supplier')}
              placeholder="Nome fornitore"
            />
            <Input
              label="Barcode"
              {...register('barcode')}
              placeholder="EAN13, QR..."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Descrizione</label>
            <textarea
              {...register('description')}
              rows={2}
              className={textareaClass}
              placeholder="Descrizione del materiale..."
            />
          </div>
          {saveMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio. Riprova.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setFormOpen(false); setEditingMaterial(null) }}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || saveMutation.isPending}>
              {saveMutation.isPending
                ? 'Salvataggio...'
                : editingMaterial
                  ? 'Salva modifiche'
                  : 'Crea materiale'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal numeri seriali */}
      <Modal
        open={!!selectedMaterialId}
        onClose={() => setSelectedMaterialId(null)}
        title="Numeri seriali"
        size="lg"
      >
        {serialsLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--fc-primary)]" />
          </div>
        ) : !serialsData || serialsData.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--fc-foreground-muted)]">
            Nessun numero seriale registrato per questo materiale.
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded border border-[var(--fc-border)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[var(--fc-surface-overlay)]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">S/N</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">P/N</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Location</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Stato</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Note</th>
                </tr>
              </thead>
              <tbody>
                {serialsData.map((s) => {
                  const statusConfig: Record<MaterialSerial['status'], { label: string; className: string }> = {
                    available: { label: 'Disponibile', className: 'bg-green-100 text-green-700' },
                    in_use: { label: 'In uso', className: 'bg-yellow-100 text-yellow-700' },
                    transferred: { label: 'Trasferito', className: 'bg-blue-100 text-blue-700' },
                    damaged: { label: 'Danneggiato', className: 'bg-red-100 text-red-700' },
                  }
                  const { label, className } = statusConfig[s.status] ?? { label: s.status, className: '' }
                  return (
                    <tr key={s.id} className="border-t border-[var(--fc-border)]">
                      <td className="px-3 py-2 font-mono text-xs text-[var(--fc-foreground)]">{s.serialNumber}</td>
                      <td className="px-3 py-2 font-mono text-xs text-[var(--fc-foreground-muted)]">{s.partNumber ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-[var(--fc-foreground-muted)]">{s.location ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${className}`}>
                          {label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--fc-foreground-muted)]">{s.notes ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal QR code */}
      {qrMaterial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-xs rounded-2xl bg-[var(--fc-surface)] p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setQrMaterial(null)}
              className="absolute right-4 top-4 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="mb-1 text-base font-semibold text-[var(--fc-foreground)]">QR Code</h3>
            <p className="mb-4 text-xs text-[var(--fc-foreground-muted)]">
              {qrMaterial.code} — {qrMaterial.name}
              {qrMaterial.location ? ` · ${qrMaterial.location}` : ''}
            </p>
            <div ref={qrRef} className="flex justify-center rounded-xl bg-white p-4">
              <QRCode value={qrValue} size={200} />
            </div>
            <p className="mt-3 text-center text-xs text-[var(--fc-foreground-muted)]">
              Scansiona per identificare il materiale
            </p>
            <Button
              className="mt-4 w-full"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleDownloadQr}
            >
              Scarica SVG
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
